/**
 * Creator Review — Loox Storefront API renderer
 * Visual reference: tittan13toy-templates/community-v2.html (.community2-review-card)
 *
 * - Fetches reviews once per store ID per page (in-flight dedupe)
 * - SessionStorage TTL cache (data-cache-minutes)
 * - Filters: min rating, only-with-images, product_id
 * - Sorts: newest / oldest / highest-rating / lowest-rating
 * - Renders comic-book sticker cards with optional bursts + rotation
 * - Slider mode (scroll-snap + arrows + optional autoplay)
 * - Honors prefers-reduced-motion
 */
(function () {
  'use strict';

  var API_BASE = 'https://storefront-api.loox.io/storefront/v1/store';
  var CACHE_PREFIX = 'cr_loox_';
  var inflight = new Map();

  var BURST_LABELS = ['POW!', 'BAM!', 'ZAP!', 'BOOM!'];

  var prefersReducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---------- fetch + cache ----------
  // Loox Storefront API supports product_id, per_page and page query params.
  // min_rating / photo filters are NOT server-side — they run client-side below.
  function fetchReviews(storeId, cacheMinutes, productId) {
    // Product-specific pools are cached separately from the store-wide pool.
    var cacheKey = CACHE_PREFIX + storeId + (productId ? '_' + productId : '');

    try {
      var raw = sessionStorage.getItem(cacheKey);
      if (raw) {
        var cached = JSON.parse(raw);
        if (cached && Date.now() - cached.ts < cacheMinutes * 60 * 1000) {
          return Promise.resolve(cached.data);
        }
      }
    } catch (_) {}

    if (inflight.has(cacheKey)) return inflight.get(cacheKey);

    var url = API_BASE + '/' + encodeURIComponent(storeId) + '/product-reviews';
    var params = new URLSearchParams();
    if (productId) params.set('product_id', productId);
    params.set('per_page', '50'); // fetch a large pool to filter/pin from
    params.set('page', '1');
    url += '?' + params.toString();

    var p = fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'omit'
    })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (json) {
        var reviews = (json && json.reviews) || [];
        try {
          sessionStorage.setItem(
            cacheKey,
            JSON.stringify({ ts: Date.now(), data: reviews })
          );
        } catch (_) {}
        return reviews;
      })
      .finally(function () {
        inflight.delete(cacheKey);
      });

    inflight.set(cacheKey, p);
    return p;
  }

  // ---------- defensive field pickers ----------
  function pickId(r) {
    return String((r && (r.id || r.review_id || r.reviewId || r._id)) || '');
  }

  function pickRating(r) {
    return Number(r.rating || r.stars || r.score || 0);
  }

  function pickCreated(r) {
    return new Date(r.created_at || r.createdAt || r.date || 0).getTime() || 0;
  }

  function pickImages(r) {
    var imgs = r.images || r.photos || r.media || [];
    if (!Array.isArray(imgs)) return [];
    return imgs
      .map(function (i) {
        if (typeof i === 'string') return i;
        if (i && (i.type === 'video' || (i.url || '').match(/\.(mp4|webm|mov)(\?|$)/i))) return null;
        return i && (i.url || i.src || i.thumbnail || i.image_url);
      })
      .filter(Boolean);
  }

  function pickVideoUrl(r) {
    if (r.video || r.video_url) return r.video || r.video_url;
    var media = r.media || [];
    if (!Array.isArray(media)) return '';
    for (var i = 0; i < media.length; i++) {
      var m = media[i] || {};
      var url = m.url || m.src || '';
      if (m.type === 'video') return url;
      if (typeof url === 'string' && /\.(mp4|webm|mov)(\?|$)/i.test(url)) return url;
    }
    return '';
  }

  function pickProductLink(r) {
    if (r.product && r.product.url) return r.product.url;
    var handle = r.product_handle || (r.product && r.product.handle);
    if (handle) return '/products/' + handle;
    return '';
  }

  function pickProductName(r) {
    return (
      r.product_title ||
      r.product_name ||
      (r.product && (r.product.title || r.product.name)) ||
      ''
    );
  }

  // ---------- filter + sort ----------
  // When skipLimit is true the result is NOT sliced to opts.limit — the caller
  // slices after pinned reviews have been merged in (see applyPinned + init).
  function applyFilters(reviews, opts, skipLimit) {
    var list = reviews.slice();

    if (opts.minRating > 0) {
      list = list.filter(function (r) { return pickRating(r) >= opts.minRating; });
    }
    if (opts.onlyWithImages) {
      list = list.filter(function (r) { return pickImages(r).length > 0; });
    }
    if (opts.productId) {
      list = list.filter(function (r) {
        var pid = String(r.product_id || r.productId || (r.product && r.product.id) || '');
        return pid === String(opts.productId);
      });
    }

    switch (opts.sort) {
      case 'oldest':
        list.sort(function (a, b) { return pickCreated(a) - pickCreated(b); });
        break;
      case 'highest-rating':
        list.sort(function (a, b) { return pickRating(b) - pickRating(a); });
        break;
      case 'lowest-rating':
        list.sort(function (a, b) { return pickRating(a) - pickRating(b); });
        break;
      case 'newest':
      default:
        list.sort(function (a, b) { return pickCreated(b) - pickCreated(a); });
    }

    if (!skipLimit && opts.limit > 0) list = list.slice(0, opts.limit);
    return list;
  }

  // ---------- pinned reviews ----------
  // Move merchant-pinned review IDs to the front, in the order listed.
  // Pinned reviews are pulled from the full pool so an explicit pin is honored
  // even if it would be excluded by the rating / photo filters. Missing IDs are
  // warned about and skipped gracefully so the render never breaks.
  function applyPinned(pool, filteredSorted, opts) {
    if (!opts.pinnedIds || !opts.pinnedIds.length) return filteredSorted;

    var byId = {};
    for (var i = 0; i < pool.length; i++) {
      var id = pickId(pool[i]);
      if (id && !byId[id]) byId[id] = pool[i];
    }

    var pinned = [];
    var seen = {};
    opts.pinnedIds.forEach(function (pid) {
      var match = byId[pid];
      if (!match) {
        if (window.console && console.warn) {
          console.warn('[creator-review] Pinned review ID not found in pool:', pid);
        }
        return;
      }
      var key = pickId(match);
      if (!seen[key]) {
        seen[key] = true;
        pinned.push(match);
      }
    });

    // Append the remaining filtered reviews, skipping any already pinned.
    var rest = filteredSorted.filter(function (r) {
      return !seen[pickId(r)];
    });

    return pinned.concat(rest);
  }

  // ---------- escape ----------
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatDate(value) {
    if (!value) return '';
    var ts = pickCreated({ created_at: value });
    if (!ts) return '';
    try {
      return new Date(ts).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric'
      });
    } catch (_) {
      return '';
    }
  }

  // ---------- partial renderers ----------
  function renderStars(rating) {
    var r = Math.max(0, Math.min(5, Math.round(rating || 0)));
    var html = '<div class="creator-review__rating" role="img" aria-label="' + r + ' out of 5 stars">';
    for (var i = 1; i <= 5; i++) {
      html +=
        '<svg class="creator-review__star' + (i <= r ? ' is-filled' : '') +
        '" viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
        '<path d="M10 1.5l2.6 5.27 5.82.85-4.21 4.1.99 5.8L10 14.77l-5.2 2.74.99-5.8L1.58 7.62l5.82-.85L10 1.5z"/>' +
        '</svg>';
    }
    return html + '</div>';
  }

  function renderGallery(images) {
    if (!images.length) return '';
    return (
      '<div class="creator-review__gallery">' +
      images.slice(0, 4).map(function (src) {
        return (
          '<img class="creator-review__gallery-img" loading="lazy" decoding="async" src="' +
          esc(src) + '" alt="" />'
        );
      }).join('') +
      '</div>'
    );
  }

  function renderHandleIcon() {
    /* Shopping-bag icon — Loox reviews come from store purchases */
    return (
      '<span class="creator-review__handle-icon" aria-hidden="true">' +
      '<svg viewBox="0 0 24 24" fill="currentColor">' +
      '<path d="M6 7V6a6 6 0 0 1 12 0v1h2.5l1.5 13a2 2 0 0 1-2 2.2H4a2 2 0 0 1-2-2.2L3.5 7H6zm2 0h8V6a4 4 0 1 0-8 0v1z"/>' +
      '</svg></span>'
    );
  }

  function renderVerifiedIcon() {
    return (
      '<span class="creator-review__verified" aria-label="Verified Buyer">' +
      '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">' +
      '<path d="M12 2 9.5 4.5 6 4l-1 3.5L2 9l2 3-2 3 3 1.5L6 20l3.5-.5L12 22l2.5-2.5L18 20l1-3.5L22 15l-2-3 2-3-3-1.5L18 4l-3.5.5L12 2zm-1 14-4-4 1.4-1.4L11 13.2l5.6-5.6L18 9l-7 7z"/>' +
      '</svg></span>'
    );
  }

  function renderAvatar(name, avatarUrl) {
    if (avatarUrl) {
      return (
        '<span class="creator-review__avatar">' +
        '<img class="creator-review__avatar-img" loading="lazy" decoding="async" src="' +
        esc(avatarUrl) + '" alt="' + name + '"/></span>'
      );
    }
    return (
      '<span class="creator-review__avatar" aria-hidden="true">' +
      name.charAt(0).toUpperCase() + '</span>'
    );
  }

  function deriveWatchLink(review, opts) {
    var video = pickVideoUrl(review);
    if (video) return video;
    if (opts.watchFallback) {
      // If fallback has {product_url} placeholder, substitute live product URL
      var prodUrl = pickProductLink(review);
      if (opts.watchFallback.indexOf('{product_url}') !== -1 && prodUrl) {
        return opts.watchFallback.replace('{product_url}', prodUrl);
      }
      return opts.watchFallback;
    }
    return pickProductLink(review);
  }

  function renderWatchButton(review, opts) {
    if (!opts.showWatchButton) return '';
    var href = deriveWatchLink(review, opts);
    if (!href) return '';

    var styleClass = 'creator-review__link';
    if (opts.watchStyle && opts.watchStyle !== 'accent') {
      styleClass += ' creator-review__link--' + opts.watchStyle;
    }

    var playIcon =
      '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" class="play-icon" fill="currentColor" viewBox="0 0 256 256"><path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm48.24-94.78-64-40A8,8,0,0,0,100,88v80a8,8,0,0,0,12.24,6.78l64-40a8,8,0,0,0,0-13.56ZM116,153.57V102.43L156.91,128Z"/></svg>';

    var arrow = opts.watchArrow
      ? '<svg class="creator-review__link-arrow" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>'
      : '';

    return (
      '<a class="' + styleClass + '" href="' + esc(href) + '#scroll-to-loox" target="_blank" rel="noopener noreferrer">' +
      playIcon + esc(opts.watchLabel) + arrow + '</a>'
    );
  }

  // ---------- card renderer ----------
  function renderCard(review, index, opts) {
    var name = esc(
      review.reviewer_name ||
        review.author ||
        review.name ||
        (review.reviewer && review.reviewer.name) ||
        'Verified Buyer'
    );
    var rating = pickRating(review);
    var text = esc(review.review || review.body || review.text || review.content || '');
    var avatar =
      review.avatar_url || review.avatar ||
      review.reviewer_avatar_url ||
      (review.reviewer && review.reviewer.avatar_url) || '';
    var verified =
      review.verified === true ||
      review.verified_buyer === true ||
      review.is_verified === true;
    var productName = pickProductName(review);
    var dateStr = formatDate(review.created_at || review.createdAt || review.date);
    var images = pickImages(review);

    /* Build header */
    var verifiedHtml = opts.showVerified && verified ? renderVerifiedIcon() : '';

    /* Handle line: prefer product → date → "Verified Buyer" */
    var subline = '';
    if (opts.showProduct && productName) subline = esc(productName);
    else if (opts.showDate && dateStr) subline = esc(dateStr);
    else if (opts.showVerified && verified) subline = 'Verified Buyer';

    var handleHtml = subline
      ? '<span class="creator-review__handle">' + renderHandleIcon() + subline + '</span>'
      : '';

    var metaInner =
      '<h3 class="creator-review__name">' + name + verifiedHtml + '</h3>' + handleHtml;

    var meta;
    var cardClass = 'creator-review creator-review--loox';
    if (opts.showAvatar) {
      cardClass += ' creator-review--has-avatar';
      meta =
        '<div class="creator-review__meta">' +
        renderAvatar(name, avatar) +
        '<div class="creator-review__identity">' + metaInner + '</div>' +
        '</div>';
    } else {
      meta = '<div class="creator-review__meta">' + metaInner + '</div>';
    }

    var burstHtml = '';
    if (opts.showBursts) {
      var burstIdx = index % BURST_LABELS.length;
      burstHtml = '<span class="creator-review__burst" aria-hidden="true">' + BURST_LABELS[burstIdx] + '</span>';
    }

    var galleryHtml = opts.showImages ? renderGallery(images) : '';
    var quoteHtml = text ? '<div class="creator-review__quote"><p>' + text + '</p></div>' : '';
    var watchHtml = renderWatchButton(review, opts);

    return (
      '<article class="' + cardClass + '" data-index="' + index + '" data-burst="' + (index % 4) + '">' +
      burstHtml +
      '<header class="creator-review__head">' +
      meta +
      renderStars(rating) +
      '</header>' +
      quoteHtml +
      galleryHtml +
      watchHtml +
      '</article>'
    );
  }

  // ---------- slider ----------
  function getStep(track) {
    var card = track.querySelector('.creator-review');
    if (!card) return 0;
    var gap = parseFloat(getComputedStyle(track).gap || getComputedStyle(track).columnGap || 0) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  function attachSlider(container) {
    var track = container.querySelector('[data-loox-track]');
    var controls = container.querySelector('[data-loox-controls]');
    var prev = container.querySelector('.creator-review-loox__nav--prev');
    var next = container.querySelector('.creator-review-loox__nav--next');
    if (!track) return;
    if (controls) controls.hidden = false;

    function scrollBy(dir) {
      var step = getStep(track);
      if (!step) return;
      var max = track.scrollWidth - track.clientWidth;
      var target = track.scrollLeft + dir * step;
      if (dir > 0 && track.scrollLeft >= max - 4) target = 0;
      if (dir < 0 && track.scrollLeft <= 4) target = max;
      track.scrollTo({ left: target, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    }

    if (prev) prev.addEventListener('click', function () { scrollBy(-1); });
    if (next) next.addEventListener('click', function () { scrollBy(1); });

    if (container.dataset.autoplay === 'true' && !prefersReducedMotion) {
      var timer = null;
      function start() { stop(); timer = setInterval(function () { scrollBy(1); }, 4500); }
      function stop() { if (timer) { clearInterval(timer); timer = null; } }
      start();
      container.addEventListener('mouseenter', stop);
      container.addEventListener('mouseleave', start);
      container.addEventListener('focusin', stop);
      container.addEventListener('focusout', start);
      track.addEventListener('touchstart', stop, { passive: true });
      track.addEventListener('touchend', start, { passive: true });
    }
  }

  // ---------- "see all reviews" reveal ----------
  function setupShowAll(container, count, opts) {
    var wrap = container.querySelector('[data-loox-showall-wrap]');
    var btn = container.querySelector('[data-loox-showall]');
    var track = container.querySelector('[data-loox-track]');
    if (!wrap || !btn || !track) return;

    // No collapse in slider mode, when disabled, or when everything already fits.
    if (opts.isSlider || !opts.showAllButton || opts.initialCount < 1 || count <= opts.initialCount) {
      wrap.hidden = true;
      return;
    }

    var cards = track.querySelectorAll('.creator-review');
    for (var i = opts.initialCount; i < cards.length; i++) {
      cards[i].hidden = true;
    }
    wrap.hidden = false;

    btn.addEventListener('click', function () {
      for (var j = opts.initialCount; j < cards.length; j++) {
        cards[j].hidden = false;
      }
      btn.setAttribute('aria-expanded', 'true');
      wrap.hidden = true;
      // Move focus to the first newly revealed card for keyboard users.
      var firstRevealed = cards[opts.initialCount];
      if (firstRevealed) {
        firstRevealed.setAttribute('tabindex', '-1');
        firstRevealed.focus();
      }
    });
  }

  // ---------- options ----------
  function readOpts(container) {
    return {
      limit: parseInt(container.dataset.limit, 10) || 6,
      minRating: parseInt(container.dataset.minRating, 10) || 0,
      onlyWithImages: container.dataset.onlyWithImages === 'true',
      productId: container.dataset.productId || '',
      pinnedIds: (container.dataset.pinnedIds || '')
        .split(',')
        .map(function (id) { return id.trim(); })
        .filter(Boolean),
      sort: container.dataset.sort || 'newest',
      isSlider: container.dataset.slider === 'true',
      showDate: container.dataset.showDate === 'true',
      showProduct: container.dataset.showProduct === 'true',
      showImages: container.dataset.showImages !== 'false',
      showVerified: container.dataset.showVerified !== 'false',
      showAvatar: container.dataset.showAvatar === 'true',
      showBursts: container.dataset.showBursts !== 'false',
      showAllButton: container.dataset.showAllButton !== 'false',
      initialCount: parseInt(container.dataset.initialCount, 10) || 4,
      showWatchButton: container.dataset.watchButton !== 'false',
      watchLabel: container.dataset.watchLabel || 'Watch the Review',
      watchArrow: container.dataset.watchArrow === 'true',
      watchFallback: container.dataset.watchFallback || '',
      watchStyle: container.dataset.watchStyle || 'accent'
    };
  }

  // ---------- init ----------
  function init(container) {
    if (container.dataset.crLooxInit === 'true') return;
    container.dataset.crLooxInit = 'true';

    var storeId = container.dataset.looxStoreId;
    var track = container.querySelector('[data-loox-track]');
    var emptyEl = container.querySelector('[data-loox-empty]');
    var errorEl = container.querySelector('[data-loox-error]');

    if (!storeId || !track) return;

    var opts = readOpts(container);
    var cacheMinutes = parseInt(container.dataset.cacheMinutes, 10);
    if (isNaN(cacheMinutes)) cacheMinutes = 5;

    function showError() {
      track.innerHTML = '';
      track.setAttribute('aria-busy', 'false');
      if (errorEl) errorEl.hidden = false;
    }

    function showEmpty() {
      track.innerHTML = '';
      track.setAttribute('aria-busy', 'false');
      if (emptyEl) emptyEl.hidden = false;
    }

    fetchReviews(storeId, cacheMinutes, opts.productId)
      .then(function (all) {
        // Filter + sort the full pool without slicing, merge pinned reviews to
        // the front, then slice to the limit so pins always survive the cut.
        var filtered = applyFilters(all, opts, true);
        var merged = applyPinned(all, filtered, opts);
        var list = opts.limit > 0 ? merged.slice(0, opts.limit) : merged;
        if (!list.length) { showEmpty(); return; }

        track.innerHTML = list.map(function (r, i) {
          return renderCard(r, i, opts);
        }).join('');
        track.setAttribute('aria-busy', 'false');

        if (opts.isSlider) attachSlider(container);
        setupShowAll(container, list.length, opts);
      })
      .catch(function (err) {
        if (window.console && console.warn) {
          console.warn('[creator-review] Loox fetch failed:', err);
        }
        showError();
      });
  }

  function boot() {
    var nodes = document.querySelectorAll('.creator-review-loox');
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:block:load', function (e) {
    var el = e.target && e.target.querySelector
      ? e.target.querySelector('.creator-review-loox')
      : null;
    if (el) init(el);
  });
})();
